/**
 * HOME, at `/`. Built from `faithfully-kept-business/design/home/Main.dc.html`, its structure and
 * its words, with ops's copy of the household in place of the sample one, as GET /api/home answers
 * it. The words are in `words.ts`.
 *
 * - THE ARTBOARD'S PLACES, IN ORDER: the household under "Your home"; the next visit, which leads to
 *   the schedule; the last clean, "Today" when it was today and its day when it was not, with the
 *   note, the cleaner's first name, each thing noticed on it as the office wrote it for her
 *   (`noticedLine`, home #19), and the four faces, each of which leads to Feedback on that clean
 *   with that face chosen; what the office has written down about each home; and the ask, whose
 *   answer comes to Messages. A clean done on a day before today is headed in the past, "Your home
 *   was cleaned", where today's says "Your home is ready" (the replay of the owner's walk,
 *   2026-10-17).
 * - THE LAST CLEAN AT EACH HOME (provisional P41). The artboard draws one card for one home; here
 *   there is a card for each clean the Worker gives, in its order: at each home she may see, every
 *   clean done on the last day one was done there, so two cleans on one day, at two homes or at one,
 *   are each asked about. With one home cleaned once that day it is the artboard's one card.
 * - THE CLEAN THAT WAITS FOR HER ANSWER COMES FIRST (provisional P49): of the cleans, those whose
 *   faces are drawn with no answer of hers standing on them (`waitsForHer`) are drawn before the
 *   ones she has answered, each group in the Worker's order, newest day first, so the card the Home
 *   tab's mark means is the first clean on Home. The mark itself is drawn here only while such a
 *   card is drawn, and the Worker says one waits, so it never points at nothing she can see.
 *   Raised by the owner's walk of 2026-10-16, replayed: the mark sat on Home over three cards, none
 *   of them said to be the one.
 * - HER ANSWER ON A LAST CLEAN (provisional P41). Her own newest answer from home lights the face
 *   she gave, whether she gave it on the clean or on its invoice, which ops counts as her answer
 *   about the clean (P47; `readLastClean` in `api.ts` reads the newer of the two), so Home never
 *   asks afresh about a clean she has just answered on its invoice. It is drawn as Feedback draws a
 *   face chosen, with "Sent to the office." under the faces in the
 *   callout an edit is said in, which is what home did and never more; one that did not go through
 *   says so, with the phone, and lights none, and so does one ops's receipt says it recorded nothing
 *   of, past its twelve answers from home on a clean, saying instead that ops already has her
 *   answers. Every face still leads to Feedback, since she may answer again, and nobody else's
 *   answer is drawn.
 * - WITH SEVERAL HOMES, which no artboard draws, the next visit names its home under its day, and
 *   each clean's card under its heading, by the home's label alone (`homeNameOf`, provisional P40),
 *   in body ink as Payments and an invoice's page name a home, since the face tapped on a card counts
 *   on that home's invoice; each face on a named card is described by that name, so the faces of two
 *   cards are never heard alike. With one home nothing is named, as drawn.
 * - ONLY WHAT OPS SENT. A row the copy does not hold, because ops withheld it or never sent it, is
 *   not drawn at all, and the line that door and alarm codes stay with the office is always drawn.
 * - A VISIT OPS SAYS CANNOT CHANGE: a clean under way says so and shows no time, and a visit the
 *   cleaner could not get into says the office will be in touch; the only thing to ask about either
 *   is a message, which is the ask at the foot of the page (the seam, section 3).
 * - A WAY TO ASK ONLY FOR WHAT THE WORKER OFFERS. The faces are drawn only for a visit that offers
 *   feedback, the ask only for a person who may write to the office, and "Change something" only
 *   on a home the Worker offers an edit of, so not while an edit of it waits on the office. The ask
 *   stays while the household's last message is still open, which the Worker says it holds: it
 *   leads to Messages, which says so where the composer would be.
 * - "CHANGE SOMETHING" opens the Home edit sheet (`components/EditHome.tsx`), built from drawn parts
 *   and marked for the owner to look at. It is drawn under the codes line, as the artboard draws it,
 *   and under each home's rows when there are several, described by that home's name. A sheet whose
 *   home a read since no longer holds, gone or not hers, as the door's 404 finds it, stays drawn on
 *   the home as it was, so what it says of that 404 is never taken away in silence; once closed,
 *   the focus goes back to "Change something", or to the page's heading where that has gone.
 * - AN EDIT IS SHOWN AS OPS HAS IT, AND NEVER AS MORE (CLAUDE.md binding rule 1). Where the newest
 *   edit of a home stands is said on that home: sent to the office until ops says it has it, then
 *   waiting for us to confirm, then the office's own words once ops has decided, and nothing of
 *   home's own beside them. What is written down changes only when ops pushes the home changed.
 * - SENT HERE. Once the door has written an edit, the sheet closes, the home says sent to the office
 *   and takes the focus so it is read out, and Home is read again. Until that read carries the
 *   edit, the home goes on saying sent. The same holds when the door answers after the sheet was
 *   closed, since what it takes to ask is held here for each home, never by the sheet
 *   (`lib/asks.ts`).
 * - WHILE THE FIRST FILL COMES DOWN: nothing yet is the holding screen, and something is Home, with
 *   one line saying the rest is on its way (provisional P21, read on the Worker).
 * - THE FIRST FILL WHILE SHE WATCHES (provisional P106, ops #81, the owner's first sign-in to
 *   production on 2026-10-19). The holding screen says it is working and reads Home again by itself
 *   (`Holding.tsx`); once the household comes, Home is drawn in its place, the route unmoved, with
 *   the focus on Home's heading, as the door and the page that says to call take it when they are
 *   drawn in place (`useHeadingFocus`), and never on a first load. While the Worker says more of
 *   the first fill may still be arriving (`firstFill`: until ops's household push says the fill is
 *   done, for a day at most, or six hours from the household's first push where ops says nothing
 *   of it), one quiet line under the household says so, and that the page updates by itself, in
 *   place of the rest on its way; and Home reads itself again, gently (`lib/readAgain.ts`), from
 *   fifteen seconds backing off to five minutes, ops's own tick, nothing while the tab is hidden or
 *   the edit sheet is open, until the Worker no longer says so, for a day in view at most
 *   (`FIRST_FILL_BOUND_MS`). It never says anything is missing.
 * - WHAT SHE CAN PAY, AT THE TOP (provisional P49), which no artboard draws: for a client with
 *   anything she can pay from home now, one line under the household with how many invoices and
 *   what they come to, ops's balances added up, and a button to Payments, where each is paid on its
 *   own. An invoice her browser says she has just paid on Stripe's page is left out, as Payments
 *   draws it under On its way (P35). With nothing she can pay, the line says so, "Nothing to pay
 *   right now.", quiet, with the way to Payments under it; but not while the household is still
 *   coming down, nor while she owes an invoice the count leaves out (`owedUncounted`): one the
 *   saved method pays, at any stage of its charge, the Zelle she told ops she is sending included,
 *   or one no way from home can pay, whose own page gives the office's number. So Home never says
 *   nothing is owed when something is. Nothing for a member, who is shown no money; and nothing
 *   here reads paid (binding rule 1). Raised by the owner's walk of 2026-10-16: "not always clear
 *   if there is outstanding payment".
 * - WHAT IS ON ITS WAY, UNDER IT (round six, slice S3, P85): each invoice owed whose payment is on
 *   its way, as Payments draws it under On its way (the Worker's `onItsWay`, her household's Zelle
 *   word or a bank payment clearing, and what her browser says she has just paid on Stripe's page),
 *   "1 invoice of $175 has a payment on its way. It shows as paid once the office has the
 *   payment.", under what she can pay or under "Nothing to pay right now.", and alone beside an
 *   invoice the count leaves out. Raised by the replay of the owner's household on 2026-10-18,
 *   where Home said "Nothing to pay right now." over her Zelle the office had not matched yet.
 * - BACK TO THE PAGE SHE CAME FOR (the owner's decision of 2026-10-19, ops #76, `docs/seam.md`
 *   P107). A signed-out visitor to a deeper page signs in from the door drawn there, and the sign-in
 *   hands Home that page in the history (`lib/cameFor.ts`, `afterSignIn`), since Home is the place
 *   that knows whether her household has come down. Once it has, Home sends her on to it in place,
 *   drawing nothing of its own on the way; while nothing has, it draws the holding screen, which
 *   reads Home again as ever, and once the household comes it sends her on to the page she came for
 *   rather than drawing Home. That page asks for her session and her membership as any page does.
 * - NOT DRAWN YET: "Every visit so far" waits for its artboard. Sign out is not on the artboard,
 *   and is not here: it moved from this page's foot to Account's (slice A1), which the header's Account
 *   button reaches from every screen, so a shared phone can be handed back signed out one tap from
 *   anywhere. The holding screen keeps its own.
 */
import { useEffect, useId, useRef, useState, type ReactNode, type Ref } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  getHome,
  type CleanAnswer,
  type HomePlace,
  type HomeWrittenDown,
  type HouseholdShown,
  type Journal,
  type LastClean as Clean,
  type Visit,
} from '../api.ts';
import { CARD_BUTTON, GHOST_BUTTON, NAVY_BUTTON } from '../components/buttons.ts';
import { Onward } from '../components/Chevron.tsx';
import { EditHome } from '../components/EditHome.tsx';
import { FaceLinks } from '../components/FaceLinks.tsx';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Report, reportSays } from '../components/Report.tsx';
import { focusPageHeading, useHeadingFocus } from '../components/RouteFocus.tsx';
import { Shell, ShownHousehold, useOnItsWay } from '../components/Shell.tsx';
import { askingFor, type Asking } from '../lib/asks.ts';
import { afterSignIn } from '../lib/cameFor.ts';
import { dayInWords } from '../lib/dates.ts';
import { onItsWayNow, payableNow } from '../lib/marks.ts';
import { useReadAgain } from '../lib/readAgain.ts';
import { dayGone, homeNameOf, visitLine } from '../lib/visits.ts';
import { PAYMENTS_PATH, SCHEDULE_PATH, WRITE_PATH } from '../paths.ts';
import {
  dollars,
  EDIT_HOME,
  FACES,
  FAILED_BEFORE_PHONE,
  HOME,
  invoicesOnTheirWay,
  invoicesToPay,
  MAY_STILL_ARRIVE,
  REQUEST,
  REST_ON_ITS_WAY,
  rooms,
  UPDATES_BY_ITSELF,
} from '../words.ts';
import { Holding } from './Holding.tsx';

/** The longest wait between two reads during a first fill: five minutes, ops's own tick. */
export const FIRST_FILL_CAP_MS = 5 * 60 * 1000;

/**
 * How long Home reads itself again in view during a first fill before it stops: a day, the longest
 * the Worker may say more is still arriving, while ops's push says the fill is not done
 * (`FIRST_FILL_NULL_HOURS`, `api/src/seam/views.ts`, ops #81); six hours where the push says
 * nothing of it (`FIRST_FILL_HOURS`). The reads stop sooner wherever the Worker stops saying so.
 */
export const FIRST_FILL_BOUND_MS = 24 * 60 * 60 * 1000;

const EYEBROW = 'font-mono text-small tracking-widest text-text-emphasis uppercase';
const CARD_TITLE = 'font-serif text-h3 font-semibold text-text-brand';

/** Nothing of the household has come down yet: the holding screen is drawn in Home's place. */
const holdingOn = ({ arriving }: HomePlace): boolean =>
  arriving.household && arriving.homes && arriving.visits;

export function Home(signed: Signed) {
  /** The page she came for, as the sign-in handed it in the history, or none (P107). */
  const cameFor = afterSignIn(useLocation().state);
  return (
    <Guarded load={getHome} signed={signed} at="home" heading={HOME.eyebrow}>
      {(home, reload) =>
        // Her household has come down, so she goes on to the page she came for, in place.
        cameFor !== null && !holdingOn(home) ? (
          <Navigate to={cameFor} replace />
        ) : (
          // The Home tab's mark, only while a clean drawn here waits for her answer.
          <ShownHousehold household={marked(home)}>
            <HomeDrawn home={home} signed={signed} reload={reload} />
          </ShownHousehold>
        )
      }
    </Guarded>
  );
}

interface DrawnProps {
  readonly home: HomePlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

function HomeDrawn({ home, signed, reload }: DrawnProps) {
  /** The home the edit sheet is open on. */
  const [open, setOpen] = useState<string | null>(null);
  /**
   * That home as it was drawn when the sheet opened, which keeps the sheet drawn where a read since
   * no longer holds it, so what the sheet says of the door's 404 is never taken away in silence.
   */
  const [seen, setSeen] = useState<HomeWrittenDown | null>(null);
  /** The same, as it is now, for a door's answer that comes after the sheet it was asked from. */
  const openNow = useRef<string | null>(null);
  /** Each edit the door wrote from this page, by the home it is about. */
  const [sentHere, setSentHere] = useState<ReadonlyMap<string, string>>(new Map());
  /** Where the focus goes once the sheet has gone: back to its button, or to what the home says. */
  const [focus, setFocus] = useState<{ readonly home: string; readonly to: 'button' | 'said' }>();
  const openers = useRef(new Map<string, HTMLButtonElement>());
  /** Each home's asking, which outlives the sheet it was asked from (`lib/asks.ts`). */
  const asks = useRef(new Map<string, Asking>());

  const openOn = (homeId: string | null) => {
    openNow.current = homeId;
    setOpen(homeId);
  };

  // Back to the button that opened the sheet, or, where a read since took it away, the home gone,
  // not hers or no longer offering an edit, to the page's heading (`focusPageHeading`).
  useEffect(() => {
    if (focus?.to !== 'button') return;
    const back = openers.current.get(focus.home);
    if (back === undefined) focusPageHeading();
    else back.focus();
  }, [focus]);

  const { arriving: coming } = home;
  const holding = holdingOn(home);
  /** The household came while she watched the holding screen, so Home's heading takes the focus. */
  const [came, setCame] = useState(false);
  const wasHolding = useRef(holding);
  useEffect(() => {
    if (wasHolding.current && !holding) setCame(true);
    wasHolding.current = holding;
  }, [holding]);
  useHeadingFocus(came);

  if (holding) {
    return <Holding onSignedOut={signed.onSignedOut} reload={reload} />;
  }

  const name = home.household.name ?? (home.homes.length === 1 ? home.homes[0]?.label : undefined);
  const cleans = waitingFirst(home.lastCleans);

  const several = home.homes.length > 1;
  // The home the sheet is open on, as Home now holds it; where a read since the sheet opened no
  // longer holds it, gone or not hers, the sheet stays drawn on it as it was (the owner,
  // 2026-10-20).
  const editing =
    open === null
      ? undefined
      : (home.homes.find((each) => each.id === open) ?? (seen?.id === open ? seen : undefined));
  const close = () => {
    if (open !== null) setFocus({ home: open, to: 'button' });
    openOn(null);
  };
  // The door may answer after the sheet has closed. The home still says sent and takes the focus,
  // and Home is still read again, unless a sheet is open on another home by then, which is left as
  // it is.
  const sent = (homeId: string, requestId: string) => {
    setSentHere((held) => new Map(held).set(homeId, requestId));
    if (openNow.current === null || openNow.current === homeId) {
      setFocus({ home: homeId, to: 'said' });
      openOn(null);
    }
    reload();
  };
  const ask = (each: HomeWrittenDown, describedBy?: string) => (
    <HomeAsk
      home={each}
      sentHere={sentHere.get(each.id)}
      focusSaid={focus?.home === each.id && focus.to === 'said'}
      describedBy={describedBy}
      onChange={() => {
        setFocus(undefined);
        setSeen(each);
        openOn(each.id);
      }}
      opener={(button) => {
        if (button) openers.current.set(each.id, button);
        else openers.current.delete(each.id);
      }}
    />
  );

  return (
    <Shell
      at="home"
      payments={home.household.payments}
      over={
        editing ? (
          <EditHome
            home={editing}
            named={several}
            household={home.household.id}
            asking={askingFor(asks.current, editing.id)}
            onClose={close}
            onSent={(requestId) => sent(editing.id, requestId)}
            onSignedOut={signed.onSignedOut}
            onStale={reload}
          />
        ) : undefined
      }
    >
      <div>
        {name === undefined ? (
          <h1 className={EYEBROW}>{HOME.eyebrow}</h1>
        ) : (
          <>
            <p className={EYEBROW}>{HOME.eyebrow}</p>
            <h1 className="mt-1 font-serif text-h2 font-semibold text-pretty text-text-body">
              {name}
            </h1>
          </>
        )}
        {home.firstFill === true ? (
          <StillArriving reload={reload} paused={open !== null} />
        ) : coming.household || coming.homes || coming.visits ? (
          <p className="mt-2 text-body text-pretty">{REST_ON_ITS_WAY}</p>
        ) : null}
      </div>

      <ToPay household={home.household} coming={coming.household} />

      {home.nextVisit ? (
        <NextVisit
          visit={home.nextVisit}
          home={homeNameOf(home.nextVisit, home.homes)}
          today={home.today}
        />
      ) : null}

      {cleans.map(({ visit, journal, answer }) => (
        <LastClean
          key={visit.id}
          visit={visit}
          journal={journal}
          answer={answer}
          today={home.today}
          home={homeNameOf(visit, home.homes)}
        />
      ))}

      <WrittenDown homes={home.homes} ask={ask} />

      {home.offers.includes('message') || home.held.includes('message') ? (
        <div>
          <Link to={WRITE_PATH} className={NAVY_BUTTON}>
            {HOME.ask}
          </Link>
          <p className="mt-2 text-center text-small text-text-muted">{HOME.answerComes}</p>
        </div>
      ) : null}
    </Shell>
  );
}

/**
 * The line under the household while more of the first fill may still be arriving (provisional
 * P106), which reads Home again while it is drawn: gently, never while `paused`, as while the edit
 * sheet is open, and gone with the line once the Worker no longer says so. Drawn as Home is read,
 * so its first wait runs from that read. Once it has read for its bound it says only that more may
 * still be arriving, since the page no longer updates by itself.
 */
function StillArriving({ reload, paused }: { reload: () => void; paused: boolean }) {
  const { stopped } = useReadAgain(reload, {
    on: !paused,
    capMs: FIRST_FILL_CAP_MS,
    boundMs: FIRST_FILL_BOUND_MS,
  });
  return (
    <p className="mt-2 text-small text-pretty text-text-muted">
      {stopped ? MAY_STILL_ARRIVE : `${MAY_STILL_ARRIVE} ${UPDATES_BY_ITSELF}`}
    </p>
  );
}

/**
 * Whether her answer about a clean stands, as Home draws it: sent from home, with ops or on its way
 * to it. One that did not go through, or in a state this build has no words for, is none.
 */
const answerStands = (answer: CleanAnswer | undefined): boolean =>
  answer?.state === 'sent' || answer?.state === 'received';

/**
 * Whether a last clean waits for her answer, as Home draws it: its faces are drawn and no answer of
 * hers stands on it, as the Worker counts one waiting (provisional P49).
 */
const waitsForHer = ({ visit, answer }: Clean): boolean =>
  visit.offers.includes('visit.feedback') && !answerStands(answer);

/** The last cleans, those that wait for her answer first, each group in the Worker's order. */
const waitingFirst = (cleans: readonly Clean[]): readonly Clean[] => [
  ...cleans.filter(waitsForHer),
  ...cleans.filter((clean) => !waitsForHer(clean)),
];

/**
 * The household the nav is marked from on Home: as the Worker gave it, but without the mark that a
 * clean waits for her answer unless a clean drawn here does (`waitsForHer`), so the mark never
 * points at nothing she can see.
 */
function marked(home: HomePlace): HouseholdShown {
  if (home.lastCleans.some(waitsForHer)) return home.household;
  const { howWasIt: _waits, ...rest } = home.household;
  return rest;
}

/** What a list of invoices comes to, by ops's balances. */
const totalOf = (invoices: readonly { readonly balanceCents: number }[]) =>
  invoices.reduce((sum, invoice) => sum + invoice.balanceCents, 0);

/**
 * What she can pay from home now, at the top of Home: how many invoices and what they come to, in
 * ops's amounts, and the way to Payments; with nothing she can pay, that there is nothing to pay
 * right now, quiet, and the way to Payments, once the household has come down and unless she owes
 * an invoice the count leaves out (`owedUncounted`), which the saved method pays or no way from
 * home can pay; and under either, each invoice owed whose payment is on its way, as Payments draws
 * it under On its way, how many and what they come to (`onItsWayNow`, round six, slice S3), drawn
 * in body weight and never as paid, which stands without "Nothing to pay right now." beside one the
 * count leaves out; and nothing for a member.
 */
function ToPay({
  household,
  coming,
}: {
  household: HomePlace['household'];
  /** The household has not come down yet. */
  coming: boolean;
}) {
  // As the place draws the payments on their way, so this says what the nav counts.
  const held = useOnItsWay();
  if (household.payments !== true) return null;
  const owed = payableNow(household, held);
  const onTheirWay = onItsWayNow(household, held);
  const onTheirWayLine = (first: boolean) =>
    onTheirWay.length === 0 ? null : (
      <p className={`${first ? '' : 'mt-2 '}text-body text-pretty text-text-body`}>
        {invoicesOnTheirWay(onTheirWay.length, dollars(totalOf(onTheirWay)))}
      </p>
    );
  if (owed.length === 0) {
    const nothing = household.owedUncounted !== true;
    if (coming || (!nothing && onTheirWay.length === 0)) return null;
    return (
      <div className="rounded-card border border-border-subtle bg-surface-raised p-5">
        {nothing ? (
          <p className="text-body text-pretty text-text-body">{HOME.nothingToPay}</p>
        ) : null}
        {onTheirWayLine(!nothing)}
        <Link to={PAYMENTS_PATH} className={`mt-4 ${CARD_BUTTON}`}>
          {HOME.goToPayments}
        </Link>
      </div>
    );
  }
  return (
    <div className="rounded-card border-2 border-border-brand bg-surface-brand-soft p-5">
      <p className="text-body font-semibold text-pretty text-text-body">
        {invoicesToPay(owed.length, dollars(totalOf(owed)))}
      </p>
      {onTheirWayLine(false)}
      <Link to={PAYMENTS_PATH} className={`mt-4 ${NAVY_BUTTON}`}>
        {HOME.goToPayments}
      </Link>
    </div>
  );
}

function NextVisit({
  visit,
  home,
  today,
}: {
  visit: Visit;
  home: string | undefined;
  today: string;
}) {
  const day = visit.date === undefined ? null : dayInWords(visit.date);
  const line = visitLine(visit, today);
  return (
    <Link
      to={SCHEDULE_PATH}
      className="flex items-center justify-between gap-3 rounded-card border border-border-subtle bg-surface-raised px-5 py-4 hover:bg-surface-accent-soft"
    >
      <div className="flex flex-col gap-0.5">
        <p className="text-small text-text-muted">{HOME.nextVisit}</p>
        {day ? <p className="text-body font-semibold text-text-body">{day}</p> : null}
        {home === undefined ? null : <p className="text-body text-text-body">{home}</p>}
        {line ? <p className="text-small text-balance text-text-body">{line}</p> : null}
      </div>
      <Onward />
    </Link>
  );
}

/**
 * A last clean: "Today" and "Your home is ready" when it was, and its day and "Your home was
 * cleaned" when it was before today, its home's name when she sees several, the note, and under it
 * "Cleaned by" the cleaner's first name, with no gap above it where there is no note (the fifth
 * client-journey walk), and the faces. The note is the office's words for her, never the cleaner's
 * own (home #18, ops #98 phase 2), so it is drawn with no quotation marks and never with "From" her
 * under it, as the artboard drew the cleaner's own words. Under them and over the faces, each thing
 * noticed on the clean, the office's line for her in ops's order (home #19, ops #98 phase 3), as
 * "10. Your home is ready, on Messages" draws "Noticed: a dripping tap in the guest bath.": the
 * card's small muted ink, as "Cleaned by" is, and that artboard's 10px under what is above it, none
 * above the first where nothing is. Her own newest answer
 * from home, on the
 * clean or on its invoice (P47), lights the face she gave, as Feedback draws a face chosen, with
 * "Sent to the office." under the faces,
 * which is what home did and no more (binding rule 1); one that did not go through says so, with
 * the phone, and lights none. One ops's receipt says it recorded nothing of, past the twelve answers
 * from home it records on a clean, lights none either, since the face she gave is not the one that
 * counts, and says ops already has her answers, with the phone, as an invoice's page does. Any
 * other state says nothing, as an edit's does not. Every face still leads to Feedback, since she
 * may answer again (provisional P41).
 */
function LastClean({
  visit,
  journal,
  answer,
  today,
  home,
}: {
  visit: Visit;
  journal: Journal | null;
  answer: CleanAnswer | undefined;
  today: string;
  home: string | undefined;
}) {
  const nameId = useId();
  const when =
    visit.date === undefined ? null : visit.date === today ? HOME.today : dayInWords(visit.date);
  // Ready today; a clean done on a day before it is said in the past.
  const title = dayGone(visit, today) ? HOME.wasCleaned : HOME.ready;
  const said = reportSays({ journal, cleaner: visit.cleaner });
  const faces = visit.offers.includes('visit.feedback');
  const failed = answer?.state === 'refused' || answer?.state === 'stopped';
  const sentFromHere = answerStands(answer);
  // Ops's receipt said it recorded nothing of it, so the face she gave is not the one that counts.
  const notRecorded = sentFromHere && answer?.recorded === false;
  const lit =
    sentFromHere && !notRecorded
      ? FACES.find(({ face }) => face === answer?.face)?.face
      : undefined;

  return (
    <section className="overflow-hidden rounded-card border-2 border-border-brand bg-surface-raised">
      <div className="border-b-2 border-border-brand bg-surface-brand-soft px-5 py-3.5">
        {when ? <p className={EYEBROW}>{when}</p> : null}
        <h2 className={CARD_TITLE}>{title}</h2>
        {home === undefined ? null : (
          <p id={nameId} className="text-body text-text-body">
            {home}
          </p>
        )}
      </div>
      {said || faces ? (
        <div className="p-5">
          {/* The note, "Cleaned by" and each thing noticed, as every report of a clean draws them. */}
          <Report journal={journal} cleaner={visit.cleaner} />
          {faces ? (
            <div className={said ? 'mt-5 border-t border-border-subtle pt-5' : undefined}>
              <h3 className="text-body font-semibold text-text-body">{HOME.howWasIt}</h3>
              <FaceLinks
                visitId={visit.id}
                lit={lit}
                describedBy={home === undefined ? undefined : nameId}
                className="mt-3"
              />
              {notRecorded ? (
                <Callout text={HOME.alreadyBeforePhone} phone />
              ) : sentFromHere ? (
                <Callout text={REQUEST.sent} />
              ) : null}
              {failed ? <Callout text={FAILED_BEFORE_PHONE} phone /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** What may be asked about one home, and where it stands, given the id of the home's name. */
type Ask = (home: HomeWrittenDown, describedBy?: string) => ReactNode;

/**
 * What the office has written down about each home, and always the line about codes. With one home,
 * what may be asked about it comes after the codes line, as the artboard draws "Change something";
 * with several, it comes under each home's own rows, and the codes line, which covers every home,
 * stands apart after the last of them under a rule, so it never reads as the last home's alone.
 */
function WrittenDown({ homes, ask }: { homes: readonly HomeWrittenDown[]; ask: Ask }) {
  const several = homes.length > 1;
  const only = several ? undefined : homes[0];
  return (
    <section className="rounded-card border border-border-subtle bg-surface-raised p-5">
      <h2 className={CARD_TITLE}>{HOME.writtenDown}</h2>
      {homes.map((home) => (
        <HomeRows key={home.id} home={home} named={several} ask={several ? ask : null} />
      ))}
      <p
        className={`${several ? 'mt-5 border-t border-border-subtle pt-4' : 'mt-1'} text-small text-text-muted`}
      >
        {HOME.codes}
      </p>
      {only ? ask(only) : null}
    </section>
  );
}

interface RowsProps {
  readonly home: HomeWrittenDown;
  readonly named: boolean;
  /** What may be asked about this home, drawn under its rows, or null when the card draws it. */
  readonly ask: Ask | null;
}

function HomeRows({ home, named, ask }: RowsProps) {
  const labelId = useId();
  const rows: [string, string | null | undefined][] = [
    [HOME.theHouse, rooms(home.bedrooms, home.bathrooms)],
    [HOME.leaveAlone, home.doNotList],
    [HOME.pets, home.pets],
    [HOME.gettingIn, home.entryNotes],
  ];
  const labelled = named && home.label !== undefined;
  return (
    <>
      <div className="mt-2 flex flex-col">
        {labelled ? (
          <h3 id={labelId} className={`mt-3 ${EYEBROW}`}>
            {home.label}
          </h3>
        ) : null}
        {rows.map(([label, value]) =>
          value ? (
            <div key={label} className="border-b border-border-subtle py-3 last:border-b-0">
              <p className="text-small text-text-muted">{label}</p>
              <p className="text-body text-pretty text-text-body">{value}</p>
            </div>
          ) : null,
        )}
      </div>
      {ask ? ask(home, labelled ? labelId : undefined) : null}
    </>
  );
}

/** What a home says about an edit of it: a line, and whether the phone follows it. */
interface Said {
  readonly text: string;
  readonly phone?: boolean;
}

/**
 * Where the newest edit of a home stands, in words, or null when there is nothing to say: ops's
 * decision with no words of the office's, a reason no line is written for, or a state this build
 * does not know. A decision is only ever the office's own words, and a request is never said to be
 * more than ops has said it is.
 */
function standing(home: HomeWrittenDown, sentHere: string | undefined): Said | null {
  if (sentHere !== undefined && !home.requests.some((request) => request.id === sentHere)) {
    return { text: REQUEST.sent };
  }
  const request = home.requests.find((each) => each.type === 'home.edit');
  if (request === undefined) return null;
  switch (request.state) {
    case 'sent':
      return { text: REQUEST.sent };
    case 'received':
      return { text: REQUEST.waiting };
    case 'accepted':
    case 'declined':
      return request.answer === undefined ? null : { text: request.answer };
    case 'cancelled':
      if (request.reason === 'withdrawn') return { text: REQUEST.withdrawn };
      if (request.reason === 'membership_revoked' && !request.mine) {
        return { text: REQUEST.revoked };
      }
      return null;
    case 'refused':
    case 'stopped':
      return { text: FAILED_BEFORE_PHONE, phone: true };
    default:
      return null;
  }
}

interface HomeAskProps {
  readonly home: HomeWrittenDown;
  /** The id of an edit the door wrote from this page about this home. */
  readonly sentHere: string | undefined;
  /** Whether what the home says takes the focus, having just been sent. */
  readonly focusSaid: boolean;
  /** The id of the home's name on the card, when it is named. */
  readonly describedBy: string | undefined;
  readonly onChange: () => void;
  /** Where the home's "Change something" is, so the focus can come back to it. */
  readonly opener: (button: HTMLButtonElement | null) => void;
}

/**
 * Home's callout, Schedule's: a line, and the phone after it when `phone` is set, then a full stop.
 * Given a ref, it may take the focus, so what it says is read out.
 */
function Callout({
  text,
  phone = false,
  ref,
}: Said & { readonly ref?: Ref<HTMLDivElement> | undefined }) {
  return (
    <div
      ref={ref}
      tabIndex={ref === undefined ? undefined : -1}
      className="mt-4 rounded-control border-2 border-border-brand bg-surface-accent-soft px-4 py-3"
    >
      <p className="text-small text-pretty text-text-body">
        {text}
        {phone ? (
          <>
            {' '}
            <Phone after="." />
          </>
        ) : null}
      </p>
    </div>
  );
}

/** Where an edit of a home stands, in Schedule's callout, and "Change something" when offered. */
function HomeAsk({ home, sentHere, focusSaid, describedBy, onChange, opener }: HomeAskProps) {
  const callout = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusSaid) callout.current?.focus();
  }, [focusSaid]);

  const said = standing(home, sentHere);
  return (
    <>
      {said ? <Callout ref={callout} text={said.text} phone={said.phone} /> : null}
      {home.offers.includes('home.edit') ? (
        <button
          ref={opener}
          type="button"
          aria-haspopup="dialog"
          aria-describedby={describedBy}
          onClick={onChange}
          className={`mt-4 ${GHOST_BUTTON}`}
        >
          {EDIT_HOME.change}
        </button>
      ) : null}
    </>
  );
}
